import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { eur } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  "w-full rounded-lg border border-[#E2DBCB]/15 bg-[#0C0D0E] px-4 py-2.5 text-sm text-[#F7F5F0] placeholder:text-[#75726B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

const INITIAL_INFO = { name: "", salon: "", email: "", phone: "", notes: "" };

export default function CartDrawer({ open, onClose, cart, onQty, onRemove, onClear }) {
  const [info, setInfo] = useState(INITIAL_INFO);
  const [sending, setSending] = useState(false);

  const total = cart.reduce((s, i) => s + i.variant.price * i.qty, 0);
  const set = (key) => (e) => setInfo((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!cart.length || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: cart.map((i) => ({
          lookup_key: i.variant.lookup_key,
          quantity: i.qty,
        })),
        origin_url: window.location.origin,
        customer_name: info.name,
        email: info.email,
        salon: info.salon,
        phone: info.phone,
        notes: info.notes,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Le paiement n'a pas pu être initialisé", {
        description: "Réessayez dans un instant.",
      });
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-[#0C0D0E]/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-[#E2DBCB]/10 bg-[#141619]"
          >
            <div className="flex items-center justify-between border-b border-[#E2DBCB]/10 px-6 py-5">
              <p className="font-serif text-xl">
                Votre panier{" "}
                <span className="text-sm text-[#75726B]">
                  ({cart.reduce((s, i) => s + i.qty, 0)})
                </span>
              </p>
              <button
                data-testid="cart-close-btn"
                onClick={onClose}
                className="text-[#B0ABA0] transition-colors hover:text-[#D4AF37]"
                aria-label="Fermer le panier"
              >
                <X size={18} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center">
                <ShoppingBag size={32} className="text-[#75726B]" />
                <p data-testid="cart-empty" className="text-sm text-[#B0ABA0]">
                  Votre panier est vide.
                </p>
                <button
                  data-testid="cart-empty-cta"
                  onClick={onClose}
                  className="rounded-full border border-[#D4AF37]/40 px-6 py-2.5 text-xs font-mono uppercase tracking-widest text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
                >
                  Découvrir la collection
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {cart.map((i) => (
                    <div
                      key={i.key}
                      data-testid={`cart-item-${i.key.replace(/__/, "-").replace(/\s/g, "-")}`}
                      className="flex gap-4 rounded-xl border border-[#E2DBCB]/10 bg-[#0C0D0E] p-4"
                    >
                      <img
                        src={i.product.image}
                        alt={i.product.name}
                        className="h-20 w-16 shrink-0 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-serif text-sm leading-snug">
                          {i.product.name}
                        </p>
                        <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#75726B]">
                          {i.variant.label}
                        </p>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-2 rounded-full border border-[#E2DBCB]/15 px-1.5 py-1">
                            <button
                              data-testid={`cart-qty-minus-${i.product.id}`}
                              onClick={() => onQty(i.key, -1)}
                              className="flex h-5 w-5 items-center justify-center text-[#B0ABA0] hover:text-[#D4AF37]"
                              aria-label="Réduire"
                            >
                              <Minus size={11} />
                            </button>
                            <span className="w-5 text-center text-xs">{i.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${i.product.id}`}
                              onClick={() => onQty(i.key, 1)}
                              className="flex h-5 w-5 items-center justify-center text-[#B0ABA0] hover:text-[#D4AF37]"
                              aria-label="Augmenter"
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                          <p className="font-serif text-sm text-[#D4AF37]">
                            {eur(i.variant.price * i.qty)}
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid={`cart-remove-${i.product.id}`}
                        onClick={() => onRemove(i.key)}
                        className="self-start text-[#75726B] transition-colors hover:text-[#9C4E3A]"
                        aria-label="Retirer l'article"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <form
                  data-testid="cart-order-form"
                  onSubmit={submit}
                  className="border-t border-[#E2DBCB]/10 px-6 py-5 space-y-3"
                >
                  <div className="mb-4 space-y-1.5 text-sm">
                    <p className="flex justify-between text-[#B0ABA0]">
                      <span>Sous-total</span>
                      <span>{eur(total)}</span>
                    </p>
                    <p className="flex justify-between text-[#B0ABA0]">
                      <span>Expédition</span>
                      <span className="text-[#D4AF37]">Offerte en France</span>
                    </p>
                    <p
                      data-testid="cart-total"
                      className="flex justify-between border-t border-[#E2DBCB]/10 pt-2 font-serif text-lg text-[#F7F5F0]"
                    >
                      <span>Total</span>
                      <span className="text-[#D4AF37]">{eur(total)}</span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      data-testid="cart-name-input"
                      required
                      value={info.name}
                      onChange={set("name")}
                      placeholder="Nom *"
                      className={INPUT_CLS}
                    />
                    <input
                      data-testid="cart-salon-input"
                      value={info.salon}
                      onChange={set("salon")}
                      placeholder="Salon"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      data-testid="cart-email-input"
                      required
                      type="email"
                      value={info.email}
                      onChange={set("email")}
                      placeholder="Email *"
                      className={INPUT_CLS}
                    />
                    <input
                      data-testid="cart-phone-input"
                      type="tel"
                      value={info.phone}
                      onChange={set("phone")}
                      placeholder="Téléphone"
                      className={INPUT_CLS}
                    />
                  </div>
                  <button
                    data-testid="cart-submit-order"
                    type="submit"
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-3.5 text-sm font-semibold text-[#0C0D0E] transition-colors hover:bg-[#C5A059] disabled:opacity-60"
                  >
                    {sending && <Loader2 size={15} className="animate-spin" />}
                    {sending ? "Redirection…" : `Payer ${eur(total)} — CB sécurisée`}
                  </button>
                  <p className="text-center text-[10px] leading-relaxed text-[#75726B]">
                    Paiement total en ligne, sécurisé par Stripe.{" "}
                    <span className="text-[#C5A059]">
                      Vous préférez payer en 2 fois ?
                    </span>{" "}
                    Envoyez votre demande par email à{" "}
                    <a
                      href="mailto:contact@tarantina.fr"
                      data-testid="cart-mailto-2x"
                      className="underline transition-colors hover:text-[#D4AF37]"
                    >
                      contact@tarantina.fr
                    </a>
                  </p>
                </form>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
